// DataGrid glue for the console: localized texts and cursor-based server pagination.
import { useState } from "react";
import { GRID_LOCALE_EN, GRID_LOCALE_TH, storedPageSize, type GridPaginationModel } from "@platform/ui";
import { useT } from "../i18n";

export const PAGE_SIZES = [25, 50, 100];

export function useGridText() {
  const { locale } = useT();
  return locale === "en" ? GRID_LOCALE_EN : GRID_LOCALE_TH;
}

/**
 * Page model for APIs with opaque `nextCursor`s: remembers the cursor of every visited page so the grid can
 * go forward and back. Changing `resetKey` (filters / sort) starts again from the first page.
 */
export function useCursorPagination(gridId: string, resetKey: string) {
  const [model, setModel] = useState<GridPaginationModel>(() => ({
    page: 0,
    pageSize: storedPageSize(gridId, PAGE_SIZES, PAGE_SIZES[0]!),
  }));
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [key, setKey] = useState(resetKey);
  if (key !== resetKey) {
    // Render-phase reset (React-supported pattern): new filters → first page.
    setKey(resetKey);
    setCursors([undefined]);
    if (model.page !== 0) setModel({ ...model, page: 0 });
  }
  return {
    model,
    cursor: cursors[model.page],
    onChange(next: GridPaginationModel, nextCursor: string | undefined) {
      if (next.pageSize !== model.pageSize || next.page === 0) {
        setCursors([undefined]);
        setModel({ page: 0, pageSize: next.pageSize });
        return;
      }
      if (next.page === model.page + 1) {
        if (!nextCursor) return;
        setCursors((c) => {
          const copy = c.slice(0, next.page);
          copy[next.page] = nextCursor;
          return copy;
        });
      }
      if (next.page <= model.page + 1) setModel(next);
    },
  };
}
